import { useState, useEffect, useRef } from 'react';
import {
  Plus, Package, Truck, Send, X, Search, ChevronDown, Eye, Ban,
  CheckCircle, Clock, AlertCircle, FileText, ChevronRight
} from '../../icons/wp-icons';
import {
  getBestellungenByProjekt, createBestellung, getBestellVorschau,
  sendeBestellung, storniereBestellung, updateLieferung
} from '../../api/bestellungApi';
import { getMaterialien } from '../../api/projektApi';
import { getAllKunden } from '../../api/kundenApi';
import Modal from '../Common/Modal';
import toast from 'react-hot-toast';

const STATUS_CONFIG = {
  ENTWURF: { label: 'Entwurf', color: 'bg-gray-100 text-gray-700', icon: FileText },
  VERSENDET: { label: 'Versendet', color: 'bg-blue-100 text-blue-700', icon: Send },
  BESTAETIGT: { label: 'Bestätigt', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  TEILGELIEFERT: { label: 'Teilgeliefert', color: 'bg-yellow-100 text-yellow-700', icon: Truck },
  VOLLSTAENDIG_GELIEFERT: { label: 'Vollständig', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  STORNIERT: { label: 'Storniert', color: 'bg-red-100 text-red-700', icon: Ban }
};

export default function ProjektBestellungen({ projektId }) {
  const [bestellungen, setBestellungen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [showVorschau, setShowVorschau] = useState(null);

  useEffect(() => {
    loadBestellungen();
  }, [projektId]);

  const loadBestellungen = async () => {
    try {
      setLoading(true);
      const data = await getBestellungenByProjekt(projektId);
      setBestellungen(data);
    } catch (error) {
      toast.error('Fehler beim Laden der Bestellungen');
    } finally {
      setLoading(false);
    }
  };

  const handleStornieren = async (id) => {
    if (!confirm('Bestellung wirklich stornieren?')) return;
    try {
      await storniereBestellung(id);
      toast.success('Bestellung storniert');
      loadBestellungen();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fehler beim Stornieren');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-lg">Bestellungen</h3>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-button font-medium hover:bg-primary-dark transition text-sm"
        >
          <Plus className="w-4 h-4" />
          Neue Bestellung
        </button>
      </div>

      {/* Liste */}
      {bestellungen.length === 0 ? (
        <div className="bg-surface rounded-card shadow-card p-8 text-center text-text-muted">
          Noch keine Bestellungen für dieses Projekt.
        </div>
      ) : (
        <div className="bg-surface rounded-card shadow-card overflow-hidden divide-y divide-border">
          {bestellungen.map((b) => {
            const conf = STATUS_CONFIG[b.status] || STATUS_CONFIG.ENTWURF;
            const StatusIcon = conf.icon;
            const gesamtNetto = b.positionen?.reduce((sum, p) =>
              sum + (p.einzelpreisNetto != null ? p.einzelpreisNetto * p.menge : 0), 0) || 0;

            return (
              <div key={b.id} className="p-4 hover:bg-gray-50 transition">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">
                        {b.referenzNummer || 'Entwurf'}
                      </span>
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${conf.color}`}>
                        <StatusIcon className="w-3 h-3" />
                        {conf.label}
                      </span>
                    </div>
                    <div className="text-sm text-text-muted mt-0.5">
                      {b.lieferant?.name} • {b._count?.positionen || b.positionen?.length || 0} Positionen
                      {gesamtNetto > 0 && ` • ${(gesamtNetto / 100).toFixed(2)} €`}
                    </div>
                    <div className="text-xs text-text-muted mt-0.5">
                      {new Date(b.erstelltAm).toLocaleDateString('de-DE')} • {b.erstelltVon?.name}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {b.status === 'ENTWURF' && (
                      <button
                        onClick={() => setShowVorschau(b)}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg transition"
                      >
                        <Eye className="w-3.5 h-3.5" />
                        Vorschau & Senden
                      </button>
                    )}
                    {['VERSENDET', 'BESTAETIGT', 'TEILGELIEFERT'].includes(b.status) && (
                      <button
                        onClick={() => setShowDetail(b)}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-gray-50 text-gray-700 hover:bg-gray-100 rounded-lg transition"
                      >
                        <Truck className="w-3.5 h-3.5" />
                        Lieferung
                      </button>
                    )}
                    {b.status !== 'STORNIERT' && b.status !== 'VOLLSTAENDIG_GELIEFERT' && (
                      <button
                        onClick={() => handleStornieren(b.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition"
                        title="Stornieren"
                      >
                        <Ban className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <CreateBestellungModal
          projektId={projektId}
          onClose={() => setShowCreate(false)}
          onSuccess={() => { setShowCreate(false); loadBestellungen(); }}
        />
      )}

      {/* Vorschau & Senden Modal */}
      {showVorschau && (
        <VorschauModal
          bestellung={showVorschau}
          onClose={() => setShowVorschau(null)}
          onSent={() => { setShowVorschau(null); loadBestellungen(); }}
        />
      )}

      {/* Lieferung erfassen Modal */}
      {showDetail && (
        <LieferungModal
          bestellung={showDetail}
          onClose={() => setShowDetail(null)}
          onUpdated={() => { setShowDetail(null); loadBestellungen(); }}
        />
      )}
    </div>
  );
}

// ==========================================
// CREATE MODAL
// ==========================================
function CreateBestellungModal({ projektId, onClose, onSuccess }) {
  const [lieferanten, setLieferanten] = useState([]);
  const [materialien, setMaterialien] = useState([]);
  const [selectedLieferant, setSelectedLieferant] = useState(null);
  const [lieferantSuche, setLieferantSuche] = useState('');
  const [showLieferantDropdown, setShowLieferantDropdown] = useState(false);
  const [positionen, setPositionen] = useState([]);
  const [notiz, setNotiz] = useState('');
  const [lieferdatum, setLieferdatum] = useState('');
  const [saving, setSaving] = useState(false);
  const [showFreiPosition, setShowFreiPosition] = useState(false);
  const [freiPosition, setFreiPosition] = useState({ bezeichnung: '', menge: 1, einheit: 'Stück', einzelpreisNetto: '' });
  const lieferantRef = useRef(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (lieferantRef.current && !lieferantRef.current.contains(e.target)) {
        setShowLieferantDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadData = async () => {
    try {
      const [kundenData, matData] = await Promise.all([
        getAllKunden({ status: 'lieferant' }),
        getMaterialien(projektId)
      ]);
      setLieferanten(kundenData.filter(k => k.status === 'lieferant'));
      setMaterialien(matData.filter(m => !m.bestellt));
    } catch (error) {
      toast.error('Fehler beim Laden');
    }
  };

  const filteredLieferanten = lieferantSuche.trim()
    ? lieferanten.filter(l =>
        l.name.toLowerCase().includes(lieferantSuche.toLowerCase()) ||
        l.email?.toLowerCase().includes(lieferantSuche.toLowerCase())
      )
    : lieferanten;

  const handleSelectLieferant = (l) => {
    setSelectedLieferant(l);
    setShowLieferantDropdown(false);
    setLieferantSuche('');

    // Unbestellte Materialien als Positionen vorausfüllen
    const matPositionen = materialien.map(m => ({
      projektMaterialId: m.id,
      katalogPositionId: m.katalogPositionId,
      bezeichnung: m.katalogPosition?.bezeichnung || m.bezeichnung || 'Material',
      menge: m.menge,
      einheit: m.einheit,
      einzelpreisNetto: m.einzelkostenNetto || null,
      selected: true
    }));
    setPositionen(matPositionen);
  };

  const togglePosition = (idx) => {
    setPositionen(prev => prev.map((p, i) =>
      i === idx ? { ...p, selected: !p.selected } : p
    ));
  };

  const addFreiPosition = () => {
    if (!freiPosition.bezeichnung.trim()) return;
    setPositionen(prev => [...prev, {
      projektMaterialId: null,
      katalogPositionId: null,
      bezeichnung: freiPosition.bezeichnung,
      menge: parseFloat(freiPosition.menge) || 1,
      einheit: freiPosition.einheit,
      einzelpreisNetto: freiPosition.einzelpreisNetto ? Math.round(parseFloat(freiPosition.einzelpreisNetto) * 100) : null,
      selected: true
    }]);
    setFreiPosition({ bezeichnung: '', menge: 1, einheit: 'Stück', einzelpreisNetto: '' });
    setShowFreiPosition(false);
  };

  const handleSubmit = async () => {
    if (!selectedLieferant) return toast.error('Bitte Lieferant auswählen');
    const selectedPos = positionen.filter(p => p.selected);
    if (selectedPos.length === 0) return toast.error('Mindestens eine Position auswählen');

    setSaving(true);
    try {
      await createBestellung(projektId, {
        lieferantId: selectedLieferant.id,
        positionen: selectedPos.map(p => ({
          projektMaterialId: p.projektMaterialId,
          katalogPositionId: p.katalogPositionId,
          bezeichnung: p.bezeichnung,
          menge: p.menge,
          einheit: p.einheit,
          einzelpreisNetto: p.einzelpreisNetto
        })),
        gewuenschtesLieferdatum: lieferdatum || null,
        notiz: notiz || null
      });
      toast.success('Bestellung als Entwurf erstellt');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fehler beim Erstellen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Neue Bestellung" size="lg">
      <div className="space-y-4">
        {/* Lieferant auswählen */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Lieferant <span className="text-danger">*</span>
          </label>
          <div className="relative" ref={lieferantRef}>
            {selectedLieferant ? (
              <div className="flex items-center justify-between w-full px-4 py-2.5 border border-border rounded-lg bg-gray-50">
                <div>
                  <span className="font-medium block">{selectedLieferant.name}</span>
                  {selectedLieferant.email && (
                    <span className="text-text-muted text-sm">{selectedLieferant.email}</span>
                  )}
                </div>
                <button type="button" onClick={() => { setSelectedLieferant(null); setPositionen([]); }}
                  className="p-1.5 hover:bg-gray-200 rounded transition">
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={lieferantSuche}
                  onChange={(e) => { setLieferantSuche(e.target.value); setShowLieferantDropdown(true); }}
                  onFocus={() => setShowLieferantDropdown(true)}
                  placeholder="Lieferant suchen..."
                  className="w-full pl-10 pr-10 py-2.5 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <button type="button" onClick={() => setShowLieferantDropdown(!showLieferantDropdown)}
                  className="absolute right-3 top-1/2 -translate-y-1/2">
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showLieferantDropdown ? 'rotate-180' : ''}`} />
                </button>
              </div>
            )}

            {showLieferantDropdown && !selectedLieferant && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {filteredLieferanten.length === 0 ? (
                  <div className="px-4 py-3 text-text-muted text-center">
                    {lieferantSuche ? 'Kein Lieferant gefunden' : 'Keine Lieferanten vorhanden'}
                  </div>
                ) : (
                  filteredLieferanten.map(l => (
                    <button key={l.id} type="button" onClick={() => handleSelectLieferant(l)}
                      className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-border last:border-b-0 transition">
                      <div className="font-medium">{l.name}</div>
                      <div className="text-sm text-text-muted">{l.email}</div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        {/* Positionen */}
        {selectedLieferant && (
          <>
            <div>
              <label className="block text-sm font-medium mb-2">Positionen</label>
              {positionen.length === 0 ? (
                <p className="text-sm text-text-muted">Keine unbestellten Materialien vorhanden.</p>
              ) : (
                <div className="space-y-2">
                  {positionen.map((pos, idx) => (
                    <label key={idx} className={`flex items-center gap-3 p-3 rounded-lg border transition cursor-pointer ${
                      pos.selected ? 'border-primary bg-primary/5' : 'border-border bg-white'
                    }`}>
                      <input
                        type="checkbox"
                        checked={pos.selected}
                        onChange={() => togglePosition(idx)}
                        className="w-4 h-4 text-primary rounded"
                      />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium">{pos.bezeichnung}</span>
                        <span className="text-xs text-text-muted ml-2">
                          {pos.menge} {pos.einheit}
                          {pos.einzelpreisNetto != null && ` • ${(pos.einzelpreisNetto / 100).toFixed(2)} €/Stk`}
                        </span>
                      </div>
                    </label>
                  ))}
                </div>
              )}

              {/* Freie Position */}
              {showFreiPosition ? (
                <div className="mt-3 p-3 border border-border rounded-lg space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="text" value={freiPosition.bezeichnung}
                      onChange={e => setFreiPosition(p => ({ ...p, bezeichnung: e.target.value }))}
                      placeholder="Bezeichnung *" className="px-3 py-2 border border-border rounded-lg text-sm col-span-2"
                    />
                    <input
                      type="number" value={freiPosition.menge} min="0.01" step="0.01"
                      onChange={e => setFreiPosition(p => ({ ...p, menge: e.target.value }))}
                      placeholder="Menge" className="px-3 py-2 border border-border rounded-lg text-sm"
                    />
                    <select value={freiPosition.einheit}
                      onChange={e => setFreiPosition(p => ({ ...p, einheit: e.target.value }))}
                      className="px-3 py-2 border border-border rounded-lg text-sm">
                      <option>Stück</option><option>m</option><option>m²</option><option>kg</option><option>Liter</option><option>Pauschale</option>
                    </select>
                    <input
                      type="number" value={freiPosition.einzelpreisNetto} min="0" step="0.01"
                      onChange={e => setFreiPosition(p => ({ ...p, einzelpreisNetto: e.target.value }))}
                      placeholder="Preis netto (€, optional)" className="px-3 py-2 border border-border rounded-lg text-sm col-span-2"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={addFreiPosition}
                      className="px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark transition">
                      Hinzufügen
                    </button>
                    <button onClick={() => setShowFreiPosition(false)}
                      className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition">
                      Abbrechen
                    </button>
                  </div>
                </div>
              ) : (
                <button onClick={() => setShowFreiPosition(true)}
                  className="mt-2 flex items-center gap-1 text-sm text-primary hover:underline">
                  <Plus className="w-3.5 h-3.5" />
                  Freie Position hinzufügen
                </button>
              )}
            </div>

            {/* Lieferdatum + Notiz */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Gewünschtes Lieferdatum</label>
                <input type="date" value={lieferdatum} onChange={e => setLieferdatum(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Notiz für Lieferant</label>
                <input type="text" value={notiz} onChange={e => setNotiz(e.target.value)}
                  placeholder="z.B. Lieferung an Baustelle..." className="w-full px-3 py-2 border border-border rounded-lg text-sm" />
              </div>
            </div>
          </>
        )}

        {/* Buttons */}
        <div className="flex gap-3 pt-4">
          <button onClick={handleSubmit} disabled={saving || !selectedLieferant}
            className="flex-1 bg-primary text-white py-3 rounded-button font-medium hover:bg-primary-dark transition disabled:opacity-50">
            {saving ? 'Wird erstellt...' : 'Entwurf erstellen'}
          </button>
          <button onClick={onClose}
            className="flex-1 bg-gray-200 text-text py-3 rounded-button font-medium hover:bg-gray-300 transition">
            Abbrechen
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ==========================================
// VORSCHAU & SENDEN MODAL
// ==========================================
function VorschauModal({ bestellung, onClose, onSent }) {
  const [vorschau, setVorschau] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [vorschauAngesehen, setVorschauAngesehen] = useState(false);

  useEffect(() => {
    loadVorschau();
  }, []);

  const loadVorschau = async () => {
    try {
      const data = await getBestellVorschau(bestellung.id);
      setVorschau(data);
      setVorschauAngesehen(true);
    } catch (error) {
      toast.error('Fehler beim Laden der Vorschau');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleSenden = async () => {
    setSending(true);
    try {
      await sendeBestellung(bestellung.id);
      toast.success('Bestellung versendet!');
      onSent();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fehler beim Versenden');
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Bestellmail-Vorschau" size="lg">
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : vorschau && (
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div className="text-sm">
              <span className="font-medium text-text-muted">An:</span> {vorschau.empfaenger}
            </div>
            <div className="text-sm">
              <span className="font-medium text-text-muted">Betreff:</span> {vorschau.betreff}
            </div>
          </div>

          <div className="border border-border rounded-lg p-4 max-h-96 overflow-y-auto">
            <div dangerouslySetInnerHTML={{ __html: vorschau.htmlBody }} />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              onClick={handleSenden}
              disabled={!vorschauAngesehen || sending}
              className="flex-1 flex items-center justify-center gap-2 bg-primary text-white py-3 rounded-button font-medium hover:bg-primary-dark transition disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
              {sending ? 'Wird versendet...' : 'Bestellung absenden'}
            </button>
            <button onClick={onClose}
              className="flex-1 bg-gray-200 text-text py-3 rounded-button font-medium hover:bg-gray-300 transition">
              Abbrechen
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ==========================================
// LIEFERUNG ERFASSEN MODAL
// ==========================================
function LieferungModal({ bestellung, onClose, onUpdated }) {
  const [positionen, setPositionen] = useState(bestellung.positionen || []);
  const [saving, setSaving] = useState(false);

  const handleUpdate = async (positionId, mengeGeliefert) => {
    setSaving(true);
    try {
      const result = await updateLieferung(bestellung.id, positionId, mengeGeliefert);
      setPositionen(result.positionen || positionen);
      toast.success('Lieferung aktualisiert');
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title={`Lieferung erfassen – ${bestellung.referenzNummer || 'Bestellung'}`}>
      <div className="space-y-3">
        {positionen.map((pos) => {
          const fortschritt = pos.menge > 0 ? (pos.mengeGeliefert / pos.menge) * 100 : 0;
          const istVollstaendig = pos.mengeGeliefert >= pos.menge;

          return (
            <div key={pos.id} className={`p-3 rounded-lg border ${istVollstaendig ? 'border-green-200 bg-green-50' : 'border-border'}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <span className="font-medium text-sm">{pos.bezeichnung}</span>
                  <span className="text-xs text-text-muted ml-2">
                    Bestellt: {pos.menge} {pos.einheit}
                  </span>
                </div>
                {istVollstaendig && <CheckCircle className="w-4 h-4 text-green-600" />}
              </div>

              <div className="flex items-center gap-3">
                <div className="flex-1 bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${istVollstaendig ? 'bg-green-500' : 'bg-blue-500'}`}
                    style={{ width: `${Math.min(fortschritt, 100)}%` }}
                  />
                </div>
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    value={pos.mengeGeliefert}
                    min="0"
                    max={pos.menge * 2}
                    step="0.01"
                    onChange={(e) => {
                      const val = parseFloat(e.target.value) || 0;
                      setPositionen(prev => prev.map(p =>
                        p.id === pos.id ? { ...p, mengeGeliefert: val } : p
                      ));
                    }}
                    onBlur={(e) => {
                      const val = parseFloat(e.target.value) || 0;
                      if (val !== (bestellung.positionen.find(p => p.id === pos.id)?.mengeGeliefert || 0)) {
                        handleUpdate(pos.id, val);
                      }
                    }}
                    className="w-20 px-2 py-1 border border-border rounded text-sm text-center"
                    disabled={saving}
                  />
                  <span className="text-xs text-text-muted">/ {pos.menge}</span>
                </div>
              </div>
            </div>
          );
        })}

        <div className="pt-4">
          <button onClick={onUpdated}
            className="w-full bg-primary text-white py-3 rounded-button font-medium hover:bg-primary-dark transition">
            Fertig
          </button>
        </div>
      </div>
    </Modal>
  );
}
